import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';
import { Api } from '../../../services/api';
import axios from 'axios';

const SignatureModal = ({ visible, onHide, onSave, savedSignature = null }) => {
    const sigCanvas = useRef({});
    const [signatureImage, setSignatureImage] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [typedSignature, setTypedSignature] = useState('');
    const [uploadedSignature, setUploadedSignature] = useState(null);
    const [saveToProfile, setSaveToProfile] = useState(false);
    const [fontStyle, setFontStyle] = useState('Dancing Script'); // Default cursive font

    const fonts = ['Dancing Script', 'Great Vibes', 'Sacramento', 'Allura'];

    // Load fonts
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script&family=Great+Vibes&family=Sacramento&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => {
             document.head.removeChild(link);
        }
    }, []);

    useEffect(() => {
        if (visible && savedSignature) { 
             // If we have a saved signature, maybe default to the Saved tab (index 3)
             // But usually user wants to sign. Let's just set the state.
             setSignatureImage(savedSignature);
             // If saved signature exists, we could default to that tab, or just make it available.
             if (savedSignature) setActiveIndex(3);
        } else if (visible) {
             setSignatureImage(null);
             setTypedSignature('');
             setUploadedSignature(null);
             if(sigCanvas.current && sigCanvas.current.clear) {
                 sigCanvas.current.clear();
             }
             setActiveIndex(0);
        }
        setSaveToProfile(false);
    }, [visible, savedSignature]);

    const save = async () => {
        let finalSignature = null;

        if (activeIndex === 0) { // Draw
            if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
                finalSignature = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
            }
        } else if (activeIndex === 1) { // Type
            if (typedSignature) {
                // Convert text to image
                finalSignature = textToImage(typedSignature, fontStyle);
            }
        } else if (activeIndex === 2) { // Upload
            if (uploadedSignature) {
                finalSignature = uploadedSignature;
            }
        } else if (activeIndex === 3) { // Saved
             finalSignature = savedSignature;
        }

        if (finalSignature) {
             setSignatureImage(finalSignature);
             onSave(finalSignature);
             
             if (saveToProfile) {
                 await saveSignatureToBackend(finalSignature);
             }
             
             onHide();
        } else {
            // Show error or alert?
            // toast?
        }
    };

    const textToImage = (text, font) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = `60px "${font}", cursive`;
        const textWidth = ctx.measureText(text).width;
        canvas.width = textWidth + 40;
        canvas.height = 100;
        
        // Transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        ctx.font = `60px "${font}", cursive`;
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);
        
        return canvas.toDataURL('image/png');
    };

    const saveSignatureToBackend = async (imgData) => {
        try {
            const user_id = localStorage.getItem('user_id');
            const token = localStorage.getItem('authToken');
            
            const url = Api.API_BASE_URL + 'users/update-signature';
            
            const body = {
                user_id: user_id,
                signature: imgData
            };
            
            await axios.post(url, body, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json'
                }
            });
            console.log("Signature saved to profile");
        } catch (error) {
            console.error("Error saving signature to profile:", error);
        }
    }

    const clear = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
        }
        setTypedSignature('');
        setUploadedSignature(null);
        setSignatureImage(null);
    };
    
    const handleFileUpload = (event) => {
        const file = event.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
            setUploadedSignature(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    return (
        <Dialog header="Signature" visible={visible} onHide={onHide} style={{width: '600px'}}>
            <div className='p-2'>
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    <TabPanel header="Draw">
                         <div className='boder1' style={{border: '1px solid #ccc', borderRadius: '4px'}}>
                            <SignaturePad
                                ref={sigCanvas}
                                canvasProps={{
                                    className: "signatureCanvas",
                                    width: 530,
                                    height: 200,
                                }}
                            />
                        </div>
                        <div className="flex justify-content-end mt-2">
                            <Button className='p-button-text p-button-sm' label="Clear" onClick={() => sigCanvas.current.clear()} />
                        </div>
                    </TabPanel>
                    <TabPanel header="Type">
                        <div className="flex flex-column gap-3 p-3">
                             <InputText 
                                value={typedSignature} 
                                onChange={(e) => setTypedSignature(e.target.value)} 
                                placeholder="Type your name"
                                className="w-full"
                             />
                             
                             <div className="flex gap-2 flex-wrap">
                                 {fonts.map(font => (
                                     <div 
                                        key={font} 
                                        onClick={() => setFontStyle(font)}
                                        style={{
                                            fontFamily: font, 
                                            fontSize: '24px', 
                                            cursor: 'pointer',
                                            padding: '10px',
                                            border: fontStyle === font ? '2px solid var(--primary-color)' : '1px solid #ddd',
                                            borderRadius: '4px'
                                        }}
                                     >
                                         Signature
                                     </div>
                                 ))}
                             </div>
                             
                             <div className="p-4 border-1 border-300 border-round flex justify-content-center align-items-center" style={{minHeight: '100px', backgroundColor: '#f9f9f9'}}>
                                 {typedSignature ? (
                                      <span style={{fontFamily: fontStyle, fontSize: '48px'}}>{typedSignature}</span>
                                 ) : (
                                     <span className="text-gray-400">Preview</span>
                                 )}
                             </div>
                        </div>
                    </TabPanel>
                    <TabPanel header="Upload">
                        <div className="p-3">
                            <FileUpload 
                                mode="basic" 
                                name="demo[]" 
                                url="/api/upload" 
                                accept="image/*" 
                                maxFileSize={1000000} 
                                onSelect={handleFileUpload}
                                customUpload
                                uploadHandler={() => {}} 
                                auto
                                chooseLabel="Browse Image"
                            />
                            {uploadedSignature && (
                                <div className="mt-3 flex justify-content-center">
                                    <img src={uploadedSignature} alt="Uploaded" style={{maxWidth: '100%', maxHeight: '150px'}} />
                                </div>
                            )}
                        </div>
                    </TabPanel>
                    {savedSignature && (
                        <TabPanel header="Saved">
                             <div className="flex flex-column align-items-center p-3">
                                 <p className="mb-2 font-bold">Your Saved Signature:</p>
                                 <img src={savedSignature} alt="Saved Signature" style={{maxWidth: '100%', maxHeight: '150px', border: '1px solid #eee'}} />
                             </div>
                        </TabPanel>
                    )}
                </TabView>

                <div className="flex align-items-center mt-3 field-checkbox">
                    <Checkbox inputId="saveProfile" onChange={e => setSaveToProfile(e.checked)} checked={saveToProfile} />
                    <label htmlFor="saveProfile" className="ml-2">Save this signature to my profile for future use</label>
                </div>

                <div className="flex justify-content-end mt-3 gap-2">
                    <Button className='p-button-text' label="Cancel" onClick={onHide} />
                    <Button className='p-2 customSave-button' label="Insert Signature" onClick={save} />
                </div>
            </div>
        </Dialog>
    );
};

export default SignatureModal;